import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useCart, useCurrency, formatPrice } from "@/lib/cart";
import { cursoImagen } from "@/lib/curso-imagen";

type Course = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  image_url: string | null;
  price_clp: number | null;
  price_usd: number | null;
  is_featured: boolean;
  external_url: string | null;
  rating: number | null;
  students_count: number | null;
  delivery: string | null;
  audience: string[] | null;
};

export function CursosList({ type }: { type: "abierto" | "empresa" }) {
  const [items, setItems] = useState<Course[] | null>(null);
  const [open, setOpen] = useState<Course | null>(null);
  const cart = useCart();
  const { currency, setCurrency } = useCurrency();

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("courses")
        .select(
          "id,slug,title,description,image_url,price_clp,price_usd,is_featured,external_url,rating,students_count,delivery,audience",
        )
        .eq("is_active", true)
        .eq("course_type", type)
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false });
      if (alive) setItems((data ?? []) as Course[]);
    })();
    return () => {
      alive = false;
    };
  }, [type]);

  // Cierra la ficha del curso con la tecla Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (items === null) {
    return <p className="text-[var(--color-on-surface-variant)]">Cargando cursos…</p>;
  }

  const empresasCTA = type === "empresa" ? (
    <div className="not-prose mb-8 bg-[var(--color-brand-purple)] text-white p-5 sm:p-6 rounded-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
      <div>
        <h3 className="text-lg font-bold">¿Quieres capacitar a tu equipo?</h3>
        <p className="text-sm text-white/80 mt-1">Agenda una reunión o selecciona directamente los cursos que necesitas.</p>
      </div>
      <div className="flex flex-col sm:flex-row w-full md:w-auto gap-2">
        <Link
          to="/contacto/reunion"
          className="px-4 py-2 text-center bg-white text-[var(--color-brand-purple)] font-bold text-xs uppercase tracking-wider rounded-md hover:brightness-110"
        >
          Agendar reunión
        </Link>
        <Link
          to="/contacto/formulario"
          search={{ interes: "Agendar cursos" } as any}
          className="px-4 py-2 text-center bg-[var(--color-brand-coral)] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:brightness-110"
        >
          Agendar cursos
        </Link>
      </div>
    </div>
  ) : null;

  const personasBar = type === "abierto" ? (
    <div className="not-prose mb-6 flex flex-wrap items-center justify-between gap-3">
      <div className="inline-flex border border-[var(--color-outline-variant)] rounded-md overflow-hidden text-xs font-bold">
        <button
          onClick={() => setCurrency("CLP")}
          className={`px-3 py-1.5 ${currency === "CLP" ? "bg-[var(--color-brand-purple)] text-white" : "bg-white text-[var(--color-on-surface-variant)]"}`}
        >
          CLP $
        </button>
        <button
          onClick={() => setCurrency("USD")}
          className={`px-3 py-1.5 ${currency === "USD" ? "bg-[var(--color-brand-purple)] text-white" : "bg-white text-[var(--color-on-surface-variant)]"}`}
        >
          USD US$
        </button>
      </div>
      <Link
        to="/carrito"
        className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--color-brand-purple)] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:brightness-110"
      >
        <span className="material-symbols-outlined text-sm">shopping_cart</span>
        Carrito ({cart.count})
      </Link>
    </div>
  ) : null;

  if (items.length === 0) {
    return (
      <>
        {personasBar}
        {empresasCTA}
        <div className="bg-[var(--color-surface-container-low)] border border-dashed border-[var(--color-outline-variant)] p-10 text-center rounded-md">
          <p className="text-[var(--color-on-surface-variant)]">
            Aún no hay cursos publicados en esta sección. Muy pronto encontrarás aquí nuestro catálogo actualizado.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {personasBar}
      {empresasCTA}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 not-prose">
        {items.map((c) => (
          <article
            key={c.id}
            className={`flex flex-col bg-white border overflow-hidden rounded-md transition-colors ${
              c.is_featured
                ? "border-[var(--color-brand-coral)] shadow-md"
                : "border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)]"
            }`}
          >
            {c.is_featured && (
              <div className="bg-[var(--color-brand-coral)] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 text-center">
                ★ {type === "empresa" ? "Curso destacado" : "Diplomado · Certificación Universidad Mayor"}
              </div>
            )}
            <button
              type="button"
              onClick={() => setOpen(c)}
              aria-label={`Ver detalle de ${c.title}`}
              className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand-coral)]"
            >
              <img
                src={cursoImagen(c.title, c.image_url)}
                alt={c.title}
                loading="lazy"
                width={1024}
                height={640}
                className="w-full h-44 object-cover"
              />
            </button>

            <div className="p-6 flex flex-col flex-1">
              <h3 className="text-lg font-bold text-[var(--color-brand-purple)]">{c.title}</h3>
              {c.description && (
                <p className="text-sm text-[var(--color-on-surface-variant)] mt-2 line-clamp-4">
                  {c.description}
                </p>
              )}
              <div className="flex-1" aria-hidden="true" />
              <button
                type="button"
                onClick={() => setOpen(c)}
                className="mt-4 inline-flex items-center gap-1 self-start text-xs font-bold uppercase tracking-wider text-[var(--color-brand-coral)] hover:underline"
              >
                Ver detalle
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
              {type === "abierto" && (
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-[var(--color-on-surface)]">
                    {formatPrice(c, currency)}
                  </span>
                  <button
                    onClick={() =>
                      cart.add({
                        id: c.id,
                        slug: c.slug,
                        title: c.title,
                        price_clp: c.price_clp,
                        price_usd: c.price_usd,
                      })
                    }
                    className="inline-flex items-center gap-1 px-3 py-2 bg-[var(--color-brand-coral)] text-white font-bold text-xs uppercase tracking-wider hover:brightness-110 transition-all rounded-md"
                  >
                    <span className="material-symbols-outlined text-sm">add_shopping_cart</span>
                    Añadir
                  </button>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center overflow-y-auto bg-black/60 p-0 sm:p-6 backdrop-blur-sm"
          onClick={() => setOpen(null)}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="curso-detalle-titulo"
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-2xl bg-white border border-[var(--color-outline-variant)] rounded-t-xl sm:rounded-md shadow-xl"
          >
            <div className="relative">
              <img
                src={cursoImagen(open.title, open.image_url)}
                alt={open.title}
                className="w-full h-44 sm:h-56 object-cover rounded-t-xl sm:rounded-t-md"
              />
              <button
                type="button"
                onClick={() => setOpen(null)}
                aria-label="Cerrar"
                className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6">
              <h3 id="curso-detalle-titulo" className="text-xl font-bold text-[var(--color-brand-purple)]">
                {open.title}
              </h3>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--color-on-surface-variant)]">
                {open.rating != null && (
                  <span className="inline-flex items-center gap-1">
                    <span className="material-symbols-outlined text-base text-[var(--color-brand-coral)]">star</span>
                    {Number(open.rating).toFixed(1)}
                  </span>
                )}
                <span>{open.students_count ?? 0} estudiantes</span>
                {open.delivery && (
                  <span className="font-semibold text-[var(--color-on-surface)]">{open.delivery}</span>
                )}
              </div>

              {open.description && (
                <div className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[var(--color-on-surface-variant)]">
                  {open.description}
                </div>
              )}

              {open.audience && open.audience.length > 0 && (
                <div className="mt-5">
                  <h4 className="text-sm font-bold text-[var(--color-on-surface)]">¿A quién va dirigido?</h4>
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {open.audience.map((a) => (
                      <li
                        key={a}
                        className="rounded-full bg-[var(--color-surface-container-low)] px-3 py-1 text-xs text-[var(--color-on-surface-variant)]"
                      >
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                {open.external_url ? (
                  <a
                    href={open.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[var(--color-brand-coral)] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:brightness-110"
                  >
                    Conoce más
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </a>
                ) : type === "empresa" ? (
                  <Link
                    to="/contacto/formulario"
                    search={{ interes: "Agendar cursos" } as any}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[var(--color-brand-coral)] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:brightness-110"
                  >
                    Agendar cursos
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </Link>
                ) : (
                  <button
                    onClick={() =>
                      cart.add({
                        id: open.id,
                        slug: open.slug,
                        title: open.title,
                        price_clp: open.price_clp,
                        price_usd: open.price_usd,
                      })
                    }
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[var(--color-brand-coral)] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:brightness-110"
                  >
                    <span className="material-symbols-outlined text-base">add_shopping_cart</span>
                    Añadir al carrito
                  </button>
                )}

                {open.external_url && type === "empresa" && (
                  <Link
                    to="/contacto/formulario"
                    search={{ interes: "Agendar cursos" } as any}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-[var(--color-brand-purple)] text-[var(--color-brand-purple)] font-bold text-xs uppercase tracking-wider rounded-md hover:bg-[var(--color-surface-container-low)]"
                  >
                    Agendar cursos
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
